import { useId, useRef, type MouseEvent, type ReactNode, type RefObject } from 'react';
import { useEscapeToClose } from '../lib/dialog-stack';
import { useDialogFocus } from '../lib/focus-trap';
import { cn } from '../lib/utils';

export interface DialogProps {
  /** The decision the dialog asks for — the heading, wired to aria-labelledby. */
  title: ReactNode;
  /** Optional sub-line under the title, wired to aria-describedby. */
  description?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  /** Footer content (the answer), pinned under the scrolling body. */
  footer?: ReactNode;
  /** md = 560px (forms), lg = 720px (tables), xl = 1024px (document viewers). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** `alertdialog` for a consequential confirm (an attestation, a delete). */
  role?: 'dialog' | 'alertdialog';
  /** Where focus lands on open; default the first control, else the panel. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Escape / backdrop do nothing while a submit is in flight. */
  busy?: boolean;
  /** Extra classes for the body region (e.g. a viewer that must not pad). */
  bodyClassName?: string;
  /** Extra classes for the header row (e.g. to add actions beside the title). */
  headerAside?: ReactNode;
}

/** A scrim click this soon after opening is the tail of the click that opened it. */
export const SCRIM_CLOSE_GRACE_MS = 500;

const SIZE: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-[560px]',
  lg: 'sm:max-w-[720px]',
  xl: 'sm:max-w-5xl',
};

/**
 * The shared modal dialog (DataRoom UI "Dialog"): role=dialog + aria-modal, a
 * labelled heading and description, focus moved in on open, trapped while open
 * and returned to the opener on close; Escape (topmost dialog only) and a
 * deliberate scrim click (pressed and released on the scrim, not in the first
 * {@link SCRIM_CLOSE_GRACE_MS} after opening) close it unless `busy`. The body scrolls while header and footer stay;
 * below 640px it becomes a bottom sheet. Presentational: the page owns the
 * open state and the actions in `footer`.
 */
export function Dialog({
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md',
  role = 'dialog',
  initialFocusRef,
  busy = false,
  bodyClassName,
  headerAside,
}: DialogProps) {
  const headingId = useId();
  const descId = useId();
  const bodyId = useId();
  // An alertdialog with no separate description is described by its body (the
  // statement of what is about to happen).
  const describedBy = description ? descId : role === 'alertdialog' ? bodyId : undefined;
  const panelRef = useRef<HTMLDivElement>(null);
  const close = () => {
    if (!busy) onClose();
  };
  // The scrim closes the dialog only on a deliberate click ON the scrim: a press
  // that started inside the panel (selecting text in a field and letting go
  // outside it) must not discard what was typed, and the second click of a
  // double-click on the button that OPENED the dialog lands on the scrim a moment
  // later — that is not a decision to close (found by the full-deal test: a
  // double-clicked "Create" silently skipped the new room's set-up).
  const openedAt = useRef(Date.now());
  const pressedOnScrim = useRef(false);
  const onScrimMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    pressedOnScrim.current = e.target === e.currentTarget;
  };
  const onScrimClick = (e: MouseEvent<HTMLDivElement>) => {
    const deliberate =
      e.target === e.currentTarget &&
      pressedOnScrim.current &&
      Date.now() - openedAt.current >= SCRIM_CLOSE_GRACE_MS;
    pressedOnScrim.current = false;
    if (deliberate) close();
  };
  useEscapeToClose(close);
  useDialogFocus(panelRef, { initialFocusRef });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-scrim sm:items-center sm:p-4"
      onMouseDown={onScrimMouseDown}
      onClick={onScrimClick}
    >
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={cn(
          'flex max-h-[92vh] w-full flex-col rounded-t-lg bg-card text-card-foreground shadow-lg focus:outline-none sm:max-h-[85vh] sm:rounded-lg sm:border',
          SIZE[size],
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 id={headingId} className="font-serif text-xl font-semibold leading-7 text-balance">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="mt-1 text-sm text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          {headerAside ? <div className="flex shrink-0 items-center gap-2">{headerAside}</div> : null}
        </div>
        <div id={bodyId} className={cn('flex-1 overflow-y-auto p-4 sm:p-6', bodyClassName)}>
          {children}
        </div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 sm:px-6">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
